// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// Controls preview: presentation only. All values originate from the existing
// tenant-scoped /dashboard/control-status/latest API; never invent unknown fields.
import React from "react";

const STATES = {
  pass: ["Pass", "pass"], passed: ["Pass", "pass"],
  fail: ["Fail", "fail"], failed: ["Fail", "fail"],
  error: ["Error", "error"], unknown: ["Unknown", "unknown"],
};

function Status({ value }) {
  const normalized = String(value || "unknown").trim().toLowerCase();
  const [label, tone] = STATES[normalized] || ["Unknown", "unknown"];
  return <span className={`controls-preview-v2__status controls-preview-v2__status--${tone}`}>{label}</span>;
}

export default function ControlsPreviewPanel({ rows = [], loading, error, framework, onOpenAll }) {
  return (
    <section className="controls-preview-v2" aria-label="Controls preview">
      <header className="controls-preview-v2__header">
        <h2>Controls</h2>
        <button type="button" className="controls-preview-v2__view-all" onClick={onOpenAll}>
          View All Controls <span aria-hidden="true">›</span>
        </button>
      </header>
      <div className="controls-preview-v2__table-window" role="region" aria-label="Control status table" tabIndex={0}>
        <table className="controls-preview-v2__table">
          <thead><tr>
            <th scope="col">Control ID</th>
            <th scope="col">Control Name</th>
            <th scope="col">Status</th>
            <th scope="col">Implementation</th>
            <th scope="col">Evidence</th>
            <th scope="col">Last Reviewed</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="controls-preview-v2__empty">Loading verified controls…</td></tr>
            ) : error ? (
              <tr><td colSpan={6} className="controls-preview-v2__empty controls-preview-v2__empty--error" role="alert">Controls unavailable: {error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="controls-preview-v2__empty">No control status records available for {framework || "this framework"}.</td></tr>
            ) : rows.map((row, index) => (
              <tr key={`${row.framework || framework}:${row.control_id}:${index}`}>
                <td className="controls-preview-v2__id">{row.control_id || "—"}</td>
                <td className="controls-preview-v2__missing" title="Official control name is not exposed by this API">—</td>
                <td><Status value={row.state}/></td>
                <td className="controls-preview-v2__missing" title="Authoritative implementation percentage not exposed by this API">—</td>
                <td className="controls-preview-v2__missing" title="Linked evidence count not exposed by this API">—</td>
                <td className="controls-preview-v2__missing" title="Formal review timestamp not exposed by this API">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <footer className="controls-preview-v2__footer">
        <span>Verified control states · {framework || "No framework selected"}</span>
        <span title="This API supplies last checked, not last reviewed. Implementation, evidence and names require authoritative fields.">— = data not exposed</span>
      </footer>
    </section>
  );
}
